/** @format */

import { memo } from 'react'

import styles from 'dir/styles/courses.module.css'
import traversingImg from 'assets/images/traversing.png'
import { CopyBlock, shadesOfPurple } from 'react-code-blocks'

export const _2 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>2 - Навигация по DOM</h2>
    <p className={styles.description}>
      DOM предоставляет широкий спектр возможностей при работе с HTML-элементом и его содержимым, но для этого сначала
      нужно получить ссылку на элемент. Доступ к DOM начинается с объекта <code className={styles.code}>document</code>,
      от него можно добраться до любых узлов.
    </p>
    <p className={styles.description}>
      Узлы HTML-дерева имеют иерархическое отношение друг к другу. Термины <b>ancestor (предок)</b>,{' '}
      <b>descendant (потомок)</b>,<b>parent (родитель)</b>, <b>child (ребенок)</b> и <b>sibling (сосед)</b> используются
      для описания отношений.
    </p>
    <ul className={styles.list}>
      <li className={styles.list__item}>В дереве узлов верхний узел называется корневым (root node). </li>
      <li className={styles.list__item}>Каждый узел, кроме root node, имеет только одного родителя. </li>
      <li className={styles.list__item}>У узла может быть сколько угодно детей. </li>
      <li className={styles.list__item}>Соседи — это узлы с общим родителем. </li>
      <li className={styles.list__item}>
        Дочерние элементы (дети) — элементы, которые лежат непосредственно внутри данного.
      </li>
      <li className={styles.list__item}>
        Потомки – все элементы, которые лежат внутри данного, вместе с их детьми, детьми их детей и так далее. То есть
        всё поддерево.
      </li>
    </ul>
    <img className={styles.img} src={traversingImg} alt="traversing" />
    <p className={styles.description}>Вы можете использовать следующие свойства для навигации между узлами. </p>
    <ul className={styles.list}>
      <li className={styles.list__item}>
        <code className={styles.code}>elem.parentNode</code> - выберет родителя elem
      </li>
      <li className={styles.list__item}>
        <code className={styles.code}>elem.childNodes</code> - псевдо-массив хранит все дочерние элементы, включая
        текстовые.
      </li>
      <li className={styles.list__item}>
        <code className={styles.code}>elem.children</code> - псевдо-массив хранит только дочерние узлы-элементы, то есть
        соответствующие тегам.
      </li>
      <li className={styles.list__item}>
        <code className={styles.code}>elem.firstChild</code> - выберет первый дочерний элемент внутри elem, включая
        текстовые узлы.
      </li>
      <li className={styles.list__item}>
        <code className={styles.code}>elem.firstElementChild</code> - выберет первый дочерний узел-элемент внутри elem.
      </li>
      <li className={styles.list__item}>
        <code className={styles.code}>elem.lastChild</code> - выберет последний дочерний элемент внутри elem, включая
        текстовые узлы.
      </li>
      <li className={styles.list__item}>
        <code className={styles.code}>elem.lastElementChild</code> - выберет последний дочерний узел-элемент внутри
        elem.
      </li>
      <li className={styles.list__item}>
        <code className={styles.code}>elem.previousSibling</code> - выберет элемент "слева" от elem (его предыдущего
        соседа)
      </li>
      <li className={styles.list__item}>
        <code className={styles.code}>elem.previousElementSibling</code> - выберет узел-элемент "слева" от elem (его
        предыдущего соседа)
      </li>
      <li className={styles.list__item}>
        <code className={styles.code}>elem.nextSibling</code> - выберет элемент "справа" от elem (его следующего соседа)
      </li>
      <li className={styles.list__item}>
        <code className={styles.code}>elem.nextElementSibling</code> - выберет узел-элемент "справа" от elem (его
        предыдущего соседа)
      </li>
    </ul>
    <p className={styles.notificationInfo}>
      DOM-коллекции, такие как <code className={styles.code}>childNodes</code> и{' '}
      <code className={styles.code}>children</code> — псевдомассивы, у них нет большинства методов массивов.{' '}
    </p>
    <a
      className={styles.link}
      target="_blank"
      rel="noopener noreferrer"
      href="https://developer.mozilla.org/en-US/docs/Web/API/Document_Object_Model/Introduction#Important_Data_Types"
    >
      Типы данных узлов и коллекций на MDN
    </a>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.1 - Поиск DOM-узлов</h3>
      <p className={styles.description}>
        Итак, мы уже знаем что DOM-узел это объект. У него есть методы и свойства. Самое время научиться находить
        элемент по произвольному CSS-селектору. Методы <code className={styles.code}>elem.querySelector*</code> это
        современный стандарт для поиска DOM-узлов. Они позволяют найти узел или группу узлов по CSS-селектору любой
        сложности.{' '}
      </p>
      <CopyBlock
        text={`elem.querySelector(selector)
`}
        language="html"
        showLineNumbers
        theme={shadesOfPurple}
      />
      <p className={styles.description}>Используется когда мы заведомо знаем, что подходящий элемент только один. </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Возвращает первый найденный элемент внутри <code className={styles.code}>elem</code>, соответствующий
          CSS-селектору <code className={styles.code}>selector</code>.{' '}
        </li>
        <li className={styles.list__item}>
          Если ничего не найдено, вернет <code className={styles.code}>null</code>.{' '}
        </li>
      </ul>
      <CopyBlock
        text={`elem.querySelectorAll(selector)
`}
        language="html"
        showLineNumbers
        theme={shadesOfPurple}
      />
      <p className={styles.description}>Используется когда мы заведомо знаем, что подходящих элементов более одного.</p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Возвращает псевдомассив всех элементов внутри <code className={styles.code}>elem</code>, удовлетворяющих
          CSS-селектору <code className={styles.code}>selector</code>.{' '}
        </li>
        <li className={styles.list__item}>Если ничего не найдено вернет пустой массив. </li>
      </ul>
      <iframe
        height="300"
        scrolling="no"
        title="module-08-querySelector*"
        src="https://codepen.io/goit-fe-adv/embed/NvVpMw?default-tab=html%2Cresult"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />{' '}
    </section>
  </section>
))
